class OurProductsGridSlider extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.track = this.querySelector('[data-our-products-track]');
    this.slides = Array.from(this.querySelectorAll('[data-our-products-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-our-products-dot]'));
    this.raf = null;

    if (!this.track || this.slides.length < 2 || !this.dots.length) return;

    this.initialized = true;
    this.onScroll = this.queueUpdate.bind(this);
    this.onResize = this.queueUpdate.bind(this);

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.scrollToSlide(index));
    });

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    this.resetInitialPosition();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);

    if (this.raf) window.cancelAnimationFrame(this.raf);
  }

  scrollToSlide(index) {
    const slide = this.slides[index];

    if (!slide) return;

    const trackRect = this.track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const left = this.track.scrollLeft + slideRect.left - trackRect.left;

    this.track.scrollTo({ left, behavior: 'smooth' });
    this.setActiveDot(index);
  }

  resetInitialPosition() {
    this.track.scrollTo({ left: 0, behavior: 'auto' });
    this.setActiveDot(0);

    window.requestAnimationFrame(() => {
      this.track.scrollTo({ left: 0, behavior: 'auto' });
      this.setActiveDot(0);
    });
  }

  queueUpdate() {
    if (this.raf) return;

    this.raf = window.requestAnimationFrame(() => {
      this.raf = null;
      this.updateActiveDot();
    });
  }

  updateActiveDot() {
    const trackRect = this.track.getBoundingClientRect();
    let activeIndex = 0;
    let closestDistance = Infinity;

    this.slides.forEach((slide, index) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - trackRect.left);

      if (distance < closestDistance) {
        closestDistance = distance;
        activeIndex = index;
      }
    });

    this.setActiveDot(activeIndex);
  }

  setActiveDot(activeIndex) {
    this.dots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
}

if (!customElements.get('our-products-grid-slider')) {
  customElements.define('our-products-grid-slider', OurProductsGridSlider);
}

(() => {
  const PARAM_NAMES = ['scroll_to', 'scroll', 'target'];

  if (!window.__ourProductsGridUnavailableBound) {
    window.__ourProductsGridUnavailableBound = true;

    document.addEventListener('click', (event) => {
      const unavailableSection = event.target.closest('.our-products-grid--unavailable');
      const link = event.target.closest('a[href]');

      if (!unavailableSection || !link || !unavailableSection.contains(link)) return;

      event.preventDefault();
      event.stopPropagation();
    });
  }

  function escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);

    return value.replace(/["\\]/g, '\\$&');
  }

  function findTarget(value) {
    if (!value) return null;

    const normalizedValue = value.replace(/^#/, '');
    const selectorValue = escapeSelector(normalizedValue);

    return document.querySelector(
      `.our-products-grid[data-scroll-target="${selectorValue}"], #${selectorValue}`
    );
  }

  function getTargetFromUrl(url) {
    for (const paramName of PARAM_NAMES) {
      const value = url.searchParams.get(paramName);
      const target = findTarget(value);

      if (target) return target;
    }

    return findTarget(url.hash);
  }

  function scrollToTarget(target, behavior = 'smooth') {
    if (!target) return false;

    target.scrollIntoView({ behavior, block: 'start' });
    return true;
  }

  function scrollFromCurrentUrl(behavior = 'auto') {
    const target = getTargetFromUrl(new URL(window.location.href));

    if (target) {
      window.requestAnimationFrame(() => scrollToTarget(target, behavior));
    }
  }

  if (!window.__ourProductsGridJumpBound) {
    window.__ourProductsGridJumpBound = true;

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');

      if (!link) return;

      const url = new URL(link.href, window.location.href);

      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;

      const target = getTargetFromUrl(url);

      if (!target) return;

      event.preventDefault();
      scrollToTarget(target);

      const nextUrl = new URL(window.location.href);
      url.searchParams.forEach((value, key) => {
        nextUrl.searchParams.set(key, value);
      });
      nextUrl.hash = url.hash;
      window.history.pushState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    });

    window.addEventListener('popstate', () => scrollFromCurrentUrl());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scrollFromCurrentUrl());
  } else {
    scrollFromCurrentUrl();
  }
})();
